import { AlertTriangle, Archive, ChevronDown, ChevronUp, HardDrive, History, Link2, Pause, Pencil, Play, Plus, Unlink } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  useCreateMonitoringTargetMutation,
	useMonitoringBindingsQuery,
  useMonitoringTargetsQuery,
	useRevokeMonitoringBindingMutation,
  useTenantSessionQuery,
  useUpdateMonitoringTargetMutation
} from "@/api/query-hooks";
import type { MonitoringTargetRecord } from "@/api/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDateTime } from "@/lib/date";

type TargetFilter = "all" | "attached" | "unbound" | "archived";

const filters: TargetFilter[] = ["all", "attached", "unbound", "archived"];

export function MonitoringTargetsPanel({ clientId, clientName }: { clientId: number; clientName?: string }) {
  const { t } = useTranslation("licensing");
  const [filter, setFilter] = useState<TargetFilter>("all");
  const query = useMonitoringTargetsQuery(clientId, filter);
  const session = useTenantSessionQuery();
  const update = useUpdateMonitoringTargetMutation(clientId);
  const create = useCreateMonitoringTargetMutation(clientId);
  const [editing, setEditing] = useState<MonitoringTargetRecord | null>(null);
  const [name, setName] = useState("");
  const [archiveTarget, setArchiveTarget] = useState<MonitoringTargetRecord | null>(null);
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createType, setCreateType] = useState<"LINUX_HOST" | "WINDOWS_HOST">("LINUX_HOST");
  const targets = query.data?.pages.flatMap((page) => page.items) ?? [];
  const counts = query.data?.pages[0]?.counts;

  const mutate = (input: Parameters<typeof update.mutate>[0], successMessage: string) => {
    update.mutate(input, {
      onSuccess: () => {
        setEditing(null);
        setArchiveTarget(null);
        toast.success(successMessage);
      },
      onError: (error) => toast.error(error instanceof Error ? error.message : t("targets.updateFailed"))
    });
  };

  return (
    <section id="monitoring-targets" className="scroll-mt-32 rounded-md border bg-panel" data-testid="monitoring-targets-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div>
          <h3 className="flex items-center gap-2 font-medium"><HardDrive className="h-4 w-4" />{t("targets.title")}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{t("targets.description")}</p>
        </div>
        <div className="flex max-w-full flex-wrap items-center justify-end gap-2">
          {session.data?.targetProvider === "LOCAL" && session.data.canManageTargets ? <Button type="button" size="sm" onClick={() => setCreating(true)}><Plus className="h-3.5 w-3.5" />{t("targets.create")}</Button> : null}
          <div className="flex max-w-full gap-1 overflow-x-auto" role="group" aria-label={t("targets.filterLabel")}>
            {filters.map((item) => (
              <Button key={item} type="button" size="sm" variant={filter === item ? "default" : "outline"} onClick={() => setFilter(item)}>
                {t(`targets.filters.${item}`)}{counts ? ` (${counts[item]})` : ""}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {query.isLoading ? <div className="px-4 py-8 text-center text-sm text-muted-foreground">{t("targets.loading")}</div> : null}
      {query.isError ? (
        <div className="flex flex-wrap items-center gap-3 px-4 py-6 text-sm">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          <span className="min-w-0 flex-1">{t("targets.loadFailed")}</span>
          <Button type="button" size="sm" variant="outline" onClick={() => void query.refetch()}>{t("targets.retry")}</Button>
        </div>
      ) : null}
      {!query.isLoading && !query.isError && targets.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-muted-foreground">{t("targets.empty")}</div>
      ) : null}

      {!query.isError && targets.length > 0 ? (
        <div className="grid gap-3 p-3 sm:grid-cols-2 xl:grid-cols-3">
          {targets.map((target) => {
            const archived = target.lifecycle === "decommissioned";
            const isEditing = editing?.id === target.id;
            return (
              <article key={target.id} className="min-w-0 rounded-md border bg-background p-3" data-testid="monitoring-target-card" data-target-id={target.id}>
                <div className="flex min-w-0 items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted"><HardDrive className="h-4 w-4" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="min-w-0 flex-1 truncate font-medium">{target.display_name}</h4>
                      <Badge variant={archived ? "destructive" : target.lifecycle === "active" ? "success" : "warning"}>
                        {t(`targets.lifecycle.${target.lifecycle}`)}
                      </Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span>{target.type}</span>
                      <span className="inline-flex items-center gap-1">
                        {target.has_active_station ? <Link2 className="h-3.5 w-3.5" /> : <Unlink className="h-3.5 w-3.5" />}
                        {target.has_active_station
                          ? t("targets.attached", { count: target.active_station_binding_count })
                          : t("targets.unbound")}
                      </span>
                    </div>
                    <div className="mt-2 truncate font-mono text-[11px] text-muted-foreground" title={target.id}>{target.id}</div>
                  </div>
                </div>

                {isEditing ? (
                  <form className="mt-3 grid gap-2" onSubmit={(event) => {
                    event.preventDefault();
                    const displayName = name.trim();
                    if (!displayName || displayName === target.display_name || update.isPending) return;
                    mutate({ targetId: target.id, displayName, expectedVersion: target.version, requestId: crypto.randomUUID() }, t("targets.renamed"));
                  }}>
                    <Input value={name} maxLength={200} aria-label={t("targets.name")} onChange={(event) => setName(event.target.value)} autoFocus />
                    <div className="flex justify-end gap-2">
                      <Button type="button" size="sm" variant="ghost" disabled={update.isPending} onClick={() => setEditing(null)}>{t("targets.cancel")}</Button>
                      <Button
                        type="submit"
                        size="sm"
                        disabled={update.isPending || !name.trim() || name.trim() === target.display_name}
                      >{t("targets.save")}</Button>
                    </div>
                  </form>
                ) : !archived ? (
                  <div className="mt-3 flex flex-wrap justify-end gap-2">
                    <Button type="button" size="sm" variant="outline" disabled={update.isPending} onClick={() => { setEditing(target); setName(target.display_name); }}>
                      <Pencil className="h-3.5 w-3.5" />{t("targets.rename")}
                    </Button>
                    {session.data?.targetProvider === "LOCAL" && target.lifecycle === "pending" ? <Button type="button" size="sm" variant="outline" disabled={update.isPending} onClick={() => mutate({ targetId: target.id, lifecycle: "active", expectedVersion: target.version, requestId: crypto.randomUUID() }, t("targets.activated"))}><Play className="h-3.5 w-3.5" />{t("targets.activate")}</Button> : null}
                    {session.data?.targetProvider === "LOCAL" && target.lifecycle === "active" ? <Button type="button" size="sm" variant="outline" disabled={update.isPending} onClick={() => mutate({ targetId: target.id, lifecycle: "suspended", expectedVersion: target.version, requestId: crypto.randomUUID() }, t("targets.suspended"))}><Pause className="h-3.5 w-3.5" />{t("targets.suspend")}</Button> : null}
                    {session.data?.targetProvider === "LOCAL" && target.lifecycle === "suspended" ? <Button type="button" size="sm" variant="outline" disabled={update.isPending} onClick={() => mutate({ targetId: target.id, lifecycle: "active", expectedVersion: target.version, requestId: crypto.randomUUID() }, t("targets.activated"))}><Play className="h-3.5 w-3.5" />{t("targets.activate")}</Button> : null}
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      disabled={update.isPending || target.has_active_station || (session.data?.targetProvider === "LOCAL" && target.lifecycle === "active")}
                      title={target.has_active_station ? t("targets.archiveBlocked") : session.data?.targetProvider === "LOCAL" && target.lifecycle === "active" ? t("targets.archiveActiveBlocked") : undefined}
                      onClick={() => setArchiveTarget(target)}
                    >
                      <Archive className="h-3.5 w-3.5" />{t("targets.archive")}
                    </Button>
                  </div>
                ) : null}

                {session.data?.targetProvider === "LOCAL" ? (
                  <LocalBindingHistory clientId={clientId} target={target} canManage={Boolean(session.data.canManageTargets)} />
                ) : null}
              </article>
            );
          })}
        </div>
      ) : null}

      {query.hasNextPage ? (
        <div className="border-t p-3 text-center">
          <Button type="button" variant="outline" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
            {query.isFetchingNextPage ? t("targets.loading") : t("targets.loadMore")}
          </Button>
        </div>
      ) : null}

      <Dialog open={creating} onOpenChange={(open) => { if (!create.isPending) setCreating(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("targets.createTitle")}</DialogTitle><DialogDescription>{t("targets.createDescription")}</DialogDescription></DialogHeader>
          <form className="grid gap-4" onSubmit={(event) => {
            event.preventDefault();
            const displayName = createName.trim();
            if (!displayName || create.isPending) return;
            create.mutate({ clientId, type: createType, displayName, requestId: crypto.randomUUID() }, {
              onSuccess: () => {
                setCreating(false);
                setCreateName("");
                setCreateType("LINUX_HOST");
                toast.success(t("targets.created"));
              },
              onError: (error) => toast.error(error instanceof Error ? error.message : t("targets.updateFailed"))
            });
          }}>
            <div className="grid gap-2"><Label htmlFor="new-target-name">{t("targets.name")}</Label><Input id="new-target-name" value={createName} maxLength={160} onChange={(event) => setCreateName(event.target.value)} autoFocus /></div>
            <div className="grid gap-2"><Label>{t("targets.type")}</Label><Select value={createType} onValueChange={(value) => setCreateType(value as typeof createType)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="LINUX_HOST">{t("targets.types.linux")}</SelectItem><SelectItem value="WINDOWS_HOST">{t("targets.types.windows")}</SelectItem></SelectContent></Select></div>
            <div className="flex justify-end gap-2"><Button type="button" variant="ghost" disabled={create.isPending} onClick={() => setCreating(false)}>{t("targets.cancel")}</Button><Button type="submit" disabled={create.isPending || !createName.trim()}>{t("targets.save")}</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={archiveTarget !== null} onOpenChange={(open) => { if (!open) setArchiveTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("targets.archiveTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("targets.archiveDescription", {
              target: archiveTarget?.display_name ?? "",
              client: clientName ?? `#${clientId}`
            })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={update.isPending}>{t("targets.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={update.isPending || archiveTarget === null}
              onClick={() => {
                if (!archiveTarget) return;
                mutate({ targetId: archiveTarget.id, lifecycle: "decommissioned", expectedVersion: archiveTarget.version, requestId: crypto.randomUUID() }, t("targets.archived"));
              }}
            >{t("targets.archive")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function LocalBindingHistory({ clientId, target, canManage }: { clientId: number; target: MonitoringTargetRecord; canManage: boolean }) {
  const { t, i18n } = useTranslation("licensing");
  const [open, setOpen] = useState(false);
  const query = useMonitoringBindingsQuery(clientId, target.id, open);
  const revoke = useRevokeMonitoringBindingMutation(clientId, target.id);
  const bindings = query.data?.pages.flatMap((page) => page.items) ?? [];
  const counts = query.data?.pages[0];

  return (
    <div className="mt-3 border-t pt-2">
      <Button type="button" size="sm" variant="ghost" className="w-full justify-between" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <span className="inline-flex items-center gap-2"><History className="h-3.5 w-3.5" />{t("targets.bindings.title")}</span>
        <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          {counts ? t("targets.bindings.counts", { active: counts.active_count, revoked: counts.revoked_count }) : null}
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </Button>
      {open ? (
        <div className="mt-2 grid gap-2" data-testid="monitoring-binding-history">
          {query.isLoading ? <div className="py-3 text-center text-xs text-muted-foreground">{t("targets.bindings.loading")}</div> : null}
          {query.isError ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/30 p-2 text-xs">
              <span className="min-w-0 flex-1">{t("targets.bindings.loadFailed")}</span>
              <Button type="button" size="sm" variant="outline" onClick={() => void query.refetch()}>{t("targets.retry")}</Button>
            </div>
          ) : null}
          {!query.isLoading && !query.isError && bindings.length === 0 ? <div className="py-3 text-center text-xs text-muted-foreground">{t("targets.bindings.empty")}</div> : null}
          {bindings.map((binding) => (
            <div key={binding.id} className="rounded-md border bg-muted/30 p-2 text-xs" data-testid="monitoring-binding-item">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="break-all font-mono font-medium">{binding.station_id}</div>
                  <div className="mt-1 text-muted-foreground">
                    {binding.status === "active"
                      ? t("targets.bindings.since", { date: formatDateTime(binding.created_at, i18n.language) })
                      : t("targets.bindings.ended", { date: formatDateTime(binding.revoked_at ?? binding.updated_at, i18n.language) })}
                  </div>
                </div>
                <Badge variant={binding.status === "active" ? "success" : "default"}>{t(`targets.bindings.status.${binding.status}`)}</Badge>
              </div>
              {binding.status === "active" && canManage ? (
                <div className="mt-2 flex justify-end">
                  <Button type="button" size="sm" variant="destructive" disabled={revoke.isPending} onClick={() => {
                    if (!window.confirm(t("targets.bindings.revokeConfirm", { station: binding.station_id }))) return;
                    revoke.mutate({ targetId: target.id, bindingId: binding.id, expectedTargetVersion: target.aggregate_revision, expectedBindingVersion: binding.version, requestId: crypto.randomUUID() }, {
                      onSuccess: () => toast.success(t("targets.bindings.revoked")),
                      onError: (error) => toast.error(error instanceof Error ? error.message : t("targets.bindings.revokeFailed"))
                    });
                  }}>{t("targets.bindings.revoke")}</Button>
                </div>
              ) : null}
            </div>
          ))}
          {query.hasNextPage ? <Button type="button" size="sm" variant="outline" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>{t("targets.loadMore")}</Button> : null}
        </div>
      ) : null}
    </div>
  );
}
